import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { millisecondsInSecond } from 'date-fns';

import { appStore, isRhinoNetworkError, isRhinoTimeoutError, RhinoError } from '@kaltura-ott/tvpil-shared';

import { WARNING_ICON } from 'consts';
import { useErrorScreenContext } from 'context/error/error';
import { useToastMessageContext } from 'context/toast/toast';
import { retryConnection } from 'hooks/App/useAppErrorReaction/utils/errorTimers';
import networkService from 'services/networkService';
import { getErrorParams, getWarningIcon } from 'utils';

const ERROR = 'Error';

export function useHandleRhinoError({
  error,
  refetch,
  isFetchEpgPrograms,
}: {
  error: RhinoError | null | undefined;
  refetch: () => Promise<boolean> | void;
  isFetchEpgPrograms: boolean;
}) {
  const intl = useIntl();
  const toastMessageContext = useToastMessageContext();
  const errorScreenContext = useErrorScreenContext();
  const warningIcon = `${appStore.staticAssetsUrl}${getWarningIcon(WARNING_ICON)}`;
  const { checkInterval } = appStore.getConnectivityConfiguration();
  const checkIntervalInSeconds = checkInterval / millisecondsInSecond;

  useEffect(() => {
    const asyncEffect = async () => {
      const isNetworkError =
        (isRhinoNetworkError(error) || isRhinoTimeoutError(error)) && !(await networkService.isNetworkConnection());

      if (isNetworkError) {
        const errorParams = getErrorParams({
          commonError: {
            code: error?.code,
            title: ERROR,
            message: ERROR,
            onRetry: refetch,
            onCancel: () => {
              appStore.clearAllApiError();
              errorScreenContext.clearError();
            },
          },
          isRhinoError: true,
          isNoConnection: isNetworkError,
          stopLoader: errorScreenContext.stopLoadingError,
        });

        errorScreenContext.setError({ ...errorParams, checkConnectionTimer: checkIntervalInSeconds });
        retryConnection(errorParams, errorScreenContext);
      }

      if (isFetchEpgPrograms && !isNetworkError) {
        toastMessageContext.showToast({
          title: intl.formatMessage({
            id: `AppErrorReaction.mediumError`,
            defaultMessage: 'Sorry, this action cannot be completed.',
          }),
          icon: warningIcon,
        });
      }
    };

    if (error) {
      asyncEffect();
    }
  }, [error]);
}
